import React from 'react';
import { subjectBorderClass as subjectBorder, subjectDotColour as subjectDot, subjectAbbrev } from '../../shared/constants/subjects';

// ─── Depth pips ───────────────────────────────────────────────────────────────

function depthPipsData(pc) {
  if (pc == null) return null;
  if (pc <= 7)  return { pips: ['light','off','off','off'], label: 'Light' };
  if (pc <= 14) return { pips: ['med','med','off','off'],   label: 'Medium' };
  return         { pips: ['heavy','heavy','heavy','heavy'], label: 'Heavy' };
}

function DepthPips({ principleCount }) {
  const data = depthPipsData(principleCount);
  if (!data) return null;
  return (
    <>
      <span className="depth-pips">
        {data.pips.map((p, i) => <span key={i} className={`depth-pip depth-pip-${p}`} />)}
      </span>
      <span className="depth-text">{data.label}</span>
    </>
  );
}

// ─── Conf colour helpers ──────────────────────────────────────────────────────

function confDoneStyle(confFraction) {
  const pct = Math.round(confFraction * 100);
  if (pct >= 70) return { background: '#DCFCE7', color: '#166534' };
  if (pct >= 40) return { background: '#FEF3C7', color: '#92400E' };
  return { background: '#FEE2E2', color: '#991B1B' };
}

function weekRevWashClass(topicConf) {
  if (topicConf == null) return 'week-card-rev-amber';
  return topicConf < 0.40 ? 'week-card-rev-red' : 'week-card-rev-amber';
}

function confClass(conf, prefix) {
  if (conf >= 0.7) return `${prefix}-green`;
  if (conf >= 0.4) return `${prefix}-amber`;
  return `${prefix}-red`;
}

// ─── Week card (v3) ───────────────────────────────────────────────────────────

export default function WeekCard({ entry, dateStr, toggles, onSwitchToDayView }) {
  const e = entry;
  const isCompleted = e.status === 'completed';
  const isMoved = e.status === 'moved';
  const isRevision = e.planner_type === 'revision';
  const borderCls = subjectBorder(e.subject_area);
  const dot = subjectDot(e.subject_area);
  const abbrev = subjectAbbrev(e.subject_area);
  const topicConf = e.topic_confidence;
  const switchTo = () => onSwitchToDayView && onSwitchToDayView(dateStr);
  const showL2 = toggles?.l2 !== false;
  const showDepth = toggles?.depth !== false;

  // ── Completed ──
  if (isCompleted) {
    const confPct = Math.round((e.confidence_on_complete ?? 0) * 100);
    const confStyle = confDoneStyle(e.confidence_on_complete ?? 0);
    return (
      <div className={`week-card-done ${borderCls}`} onClick={switchTo} role="button" tabIndex={0}>
        <div className="week-card-row1">
          <span className="week-card-name">{e.subtopic}</span>
          {e.confidence_on_complete != null && (
            <span className="week-card-done-conf" style={confStyle}>{confPct}%</span>
          )}
        </div>
        <div className="week-card-row2">
          <div className="week-card-dot" style={{ background: dot }} />
          <span className="week-card-subject">{abbrev}</span>
        </div>
      </div>
    );
  }

  // ── Moved ──
  if (isMoved) {
    return (
      <div className={`week-card-moved ${borderCls}`}>
        <div className="week-card-row1">
          <span className="week-card-name">{e.subtopic}</span>
          <span className="week-card-moved-label">Moved</span>
        </div>
        <div className="week-card-row2">
          <div className="week-card-dot" style={{ background: dot }} />
          <span className="week-card-subject">{abbrev}</span>
        </div>
      </div>
    );
  }

  // ── Revision ──
  if (isRevision) {
    return (
      <div className={`week-card-rev ${weekRevWashClass(topicConf)} ${borderCls}`} onClick={switchTo} role="button" tabIndex={0}>
        <div className="week-card-row1" style={{ position: 'relative' }}>
          <span className="week-card-name">{e.subtopic}</span>
          {topicConf != null && (
            <span className={`week-card-conf ${confClass(topicConf, 'week-card-conf')}`}>
              {Math.round(topicConf * 100)}%
            </span>
          )}
        </div>
        <div className="week-card-row2" style={{ position: 'relative' }}>
          <div className="week-card-dot" style={{ background: dot }} />
          <span className="week-card-subject">{abbrev}</span>
          {showL2 && e.general_topic && (
            <span className="week-card-l2">&middot; {e.general_topic}</span>
          )}
        </div>
        <div className="week-card-row3" style={{ position: 'relative' }}>
          <DepthPips principleCount={e.principle_count} />
          <span className="week-card-revise-pill">Revise</span>
        </div>
      </div>
    );
  }

  // ── New ──
  return (
    <div className={`week-card ${borderCls}`} onClick={switchTo} role="button" tabIndex={0}
      onKeyDown={ev => ev.key === 'Enter' && switchTo()}>
      <div className="week-card-row1">
        <span className="week-card-name">{e.subtopic}</span>
        <span className="week-card-new">New</span>
      </div>
      <div className="week-card-row2">
        <div className="week-card-dot" style={{ background: dot }} />
        <span className="week-card-subject">{abbrev}</span>
        {showL2 && e.general_topic && (
          <span className="week-card-l2">· {e.general_topic}</span>
        )}
      </div>
      {showDepth && (
        <div className="week-card-row3">
          <DepthPips principleCount={e.principle_count} />
        </div>
      )}
    </div>
  );
}
